/////////////////////////////////////////////////////////////////////
// Copyright (c) Autodesk, Inc. All rights reserved
// Written by Forge Design Automation team for Inventor
//
// Permission to use, copy, modify, and distribute this software in
// object code form for any purpose and without fee is hereby granted,
// provided that the above copyright notice appears in all copies and
// that both that copyright notice and the limited warranty and
// restricted rights notice below appear in all supporting
// documentation.
//
// AUTODESK PROVIDES THIS PROGRAM "AS IS" AND WITH ALL FAULTS.
// AUTODESK SPECIFICALLY DISCLAIMS ANY IMPLIED WARRANTY OF
// MERCHANTABILITY OR FITNESS FOR A PARTICULAR USE.  AUTODESK, INC.
// DOES NOT WARRANT THAT THE OPERATION OF THE PROGRAM WILL BE
// UNINTERRUPTED OR ERROR FREE.
/////////////////////////////////////////////////////////////////////

import * as signalR from '@aspnet/signalr';
import repo from './Repository';

class JobManager {

    async startConnection() {
        const connection = new signalR.HubConnectionBuilder()
                            .withUrl('/signalr/connection')
                            .configureLogging(signalR.LogLevel.Warning)
                            .build();

        await connection.start();
        return connection;
    }

    async doUpdateJob(projectId, parameters, onStart, onComplete, onError) {
        const connection = await this.startConnection();

        if (onStart)
            onStart();

        connection.on("onComplete", (updatedState, stats, reportUrl) => {
            function registerOrderData(updateState) {
                const parameters = updateState.parameters;

                const data = {
                    "customer": {},
                    "customer_address": {},
                    "hash": updateState.hash
                };

                const customer_data = [
                    ["Imie", "first_name"],
                    ["Nazwisko", "last_name"],
                    ["Mail_kontaktowy", "email"],
                ];
                for (const field of customer_data) {
                    data["customer"][field[1]] = parameters[field[0]]['value'].replace(/['"]+/g, '');
                }

                const customer_address_data_fields = [
                    ["Numer_kontaktowy", "phone"],
                    ["Firma", "company_name"],
                    ["Imie", "first_name"],
                    ["Nazwisko", "last_name"],
                    ["Ulica", "street"],
                    ["Miasto", "city"],
                    ["Kod_pocztowy", "zip_code"],
                    ["Kod_kraju", "country"],
                    ["NIP", "tax_identification_number"],
                ];
                for (const field of customer_address_data_fields) {
                    data["customer_address"][field[1]] = parameters[field[0]]['value'].replace(/['"]+/g, '');
                }

                const order_fields = [
                    ["Rodzaj_skrzynki", "project_type"],
                    ["Rodzaj_kasety", "casette_type"],
                    ["Ilosc_kolumn", "cols_num"],
                    ["Ilosc_wierszy", "rows_num"],
                    ["Daszek", "has_roof"],
                    ["Numeracja", "numbers_info"],
                    ["Adres_inwestycji", "investment_address"],
                    ["Uwagi1", "notes"]
                ];
                for (const field of order_fields) {
                    data[field[1]] = parameters[field[0]]['value'].replace(/['"]+/g, '');
                }
                data["has_roof"] = data["has_roof"] == "Tak";

  
                fetch('https://default1516f85710a548678a6c1f3f0586eb.0e.environment.api.powerplatform.com:443/powerautomate/automations/direct/workflows/ac2293e69e764a4e8cb1f71a287937e9/triggers/manual/paths/invoke?api-version=1&sp=%2Ftriggers%2Fmanual%2Frun&sv=1.0&sig=3wKxJSdg4ATN-eF1SBXWTQIWiIRePgUD_1wXUh6j9VY', {
                    'method': 'POST',
                    'body': JSON.stringify(data),
                    'headers': {
                        'Content-Type': 'application/json',
                    },


                })
                .then((/*response*/) => {
                    // console.log(response.status);
                });
            }
            registerOrderData(updatedState);

            // stop connection
            connection.stop();

            if (onComplete)
                onComplete(updatedState, stats, reportUrl);
        });

        connection.on("onError", (errorData) => {
            connection.stop();

            if (onError)
                onError(errorData);
        });

        await connection.invoke('CreateUpdateJob', projectId, parameters, repo.getAccessToken());
    }


    async doAdoptJob(packageId, onStart, onComplete, onError) {
        const connection = await this.startConnection();

        if (onStart)
            onStart();

        connection.on("onComplete", (newProject, stats, reportUrl) => {
            // stop connection
            connection.stop();

            if (onComplete)
                onComplete(newProject,stats,reportUrl);
        });

        connection.on("onError", (errorData) => {
            connection.stop();

            if (onError)
                onError(errorData);
        });

        await connection.invoke('CreateAdoptJob', packageId, repo.getAccessToken());
    }

    async doAdoptWithParameters(parameters, onStart, onComplete, onError) {
        const connection = await this.startConnection();

        if (onStart)
            onStart();

        connection.on("onComplete", (newProject) => {
            // stop connection
            connection.stop();

            if (onComplete)
                onComplete(newProject);
        });

        connection.on("onError", (jobId, reportUrl) => {
            connection.stop();

            if (onError)
                onError(jobId, reportUrl);
        });

        await connection.invoke("CreateAdoptProjectWithParametersJob", parameters, repo.getAccessToken());
    }

    /**
     * Generic way to generate a download and get URL to it.
     *
     * @param methodName SignalR method to call.
     * @param projectId  Project ID.
     * @param hash       Parameters hash.
     * @param onStart    Callback to be called on start. No arguments.
     * @param onSuccess  Callback to be called on success. Argument: url to the generated download.
     * @param onError    Callback to be called on error. Arguments: job ID, report url.
     * */
    async doDownloadJob(methodName, projectId, hash, key, onStart, onSuccess, onError) {

        const connection = await this.startConnection();

        if (onStart) onStart();

        connection.on("onComplete", (downloadUrl, stats, reportUrl) => {

            connection.stop();

            if (! downloadUrl && ! stats) {
                if (onError) onError("no outputs", "Downloads are not found");
                return;
            }

            if (onSuccess) {
                const token = repo.getAccessToken();
                if (token) {
                    downloadUrl += "/" + token;
                }
                onSuccess(downloadUrl, stats, reportUrl);
            }
        });

        connection.on("onError", (errorData) => {

            connection.stop();
            if (onError) onError(errorData);
        });

        if (key != null)
            await connection.invoke(methodName, projectId, hash, key, repo.getAccessToken());
        else
            await connection.invoke(methodName, projectId, hash, repo.getAccessToken());
    }

    async doDrawingExportJob(projectId, hash, drawingKey, onStart, onComplete, onSuccess, onError) {
        const connection = await this.startConnection();

        if (onStart)
            onStart();

        connection.on("onComplete", (drawingUrl, stats, reportUrl) => {

            const data = {
                "hash2": hash,
                "url": "https://pjk-config.azurewebsites.net/download/" + (encodeURIComponent(projectId))+"/"+hash+"/0/drawing.pdf",
                
                }

            fetch('https://default1516f85710a548678a6c1f3f0586eb.0e.environment.api.powerplatform.com:443/powerautomate/automations/direct/workflows/a508e993a02b437988b4c59d21d4fcb2/triggers/manual/paths/invoke?api-version=1&sp=%2Ftriggers%2Fmanual%2Frun&sv=1.0&sig=fxfHd0yaRvLDGkASY-GhMzUkHIzrBevtqMrupH6dGow', {
                'method': 'POST',
                'body': JSON.stringify(data),
                'headers': {
                    'Content-Type': 'application/json',
                },

            })
                .then((/*response*/) => {
                    // console.log(response.status);
                });


            // stop connection
            connection.stop();

            if (onComplete) {
                onComplete(drawingUrl, stats, reportUrl);
            }
        });

        connection.on("onError", (errorData) => {
            connection.stop();

            if (onError)
                onError(errorData);
        });

        await connection.invoke('CreateDrawingPdfJob', projectId, hash, drawingKey, repo.getAccessToken());
    }
}

const jobManager = new JobManager();

export function Jobs() {
  return jobManager;
}


